import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../components/Button';
import { ErrorNotice } from '../../../components/ErrorNotice';
import { Icon } from '../../../components/Icon';
import { RichTextEditor } from '../../../components/RichTextEditor';
import { useDraft } from '../../../lib/draft';
import { SnippetPicker } from '../../shortcuts/SnippetPicker';
import { COMPOSE_EVENT, type ComposeRequest } from '../compose';
import { useCreateArticle, useReplyDefaults, useUploadAttachment } from './queries';
import { RecipientField } from './RecipientField';
import { isAddress } from './recipients';
import type { ArticleType, ArticleVisibility } from './api';

interface Recipients {
  to: string[];
  cc: string[];
  bcc: string[];
}

const NO_PENDING: Record<keyof Recipients, string> = { to: '', cc: '', bcc: '' };

interface ArticleComposerProps {
  ticketId: string;
}

/**
 * Die Eingabe fuer Antwort und Notiz -- F1-08, F1-10, F1-25.
 *
 * **Die Wahl zwischen Antwort und Notiz steht oben, nicht unten.** Sie
 * entscheidet, ob der Kunde den Text liest. Eine Umschaltung neben dem
 * Absendeknopf faende der Bearbeiter erst, wenn er schon geschrieben hat --
 * und die haeufigste Verwechslung im Ticketbetrieb ist die interne Notiz, die
 * beim Kunden landet.
 *
 * **Anhaenge werden vor dem Beitrag hochgeladen** und beim Absenden
 * angehaengt. Das ist die Reihenfolge, die der Server vorgibt: Ein Anhang
 * gehoert erst dem Ticket und dann dem Beitrag. Bricht der Bearbeiter ab,
 * bleibt die Datei am Ticket -- sichtbar und loeschbar, nicht verwaist.
 *
 * **Der Text ueberlebt den geschlossenen Reiter.** Er liegt waehrend des
 * Schreibens im Browser und verschwindet, sobald er abgeschickt ist
 * (``lib/draft.ts``). Das ist nicht der Entwurf des Servers -- den gibt es
 * daneben weiterhin, und er gehoert dem Ticket. Der haeufigste Verlust im
 * Ticketbetrieb ist nicht der Serverfehler, sondern das versehentliche
 * Zurueck nach zehn Minuten Schreibarbeit.
 *
 * **Empfaenger nur bei der Antwort** (F3-22). "An" ist mit dem Ansprechpartner
 * des Tickets vorbelegt, CC mit der CC der letzten eingegangenen Mail -- wer
 * mit Kollegen in Kopie schreibt, erwartet die Antwort an alle. Beides laesst
 * sich aendern. Ohne Adresse unter "An" geht die Antwort nicht ab: Vorher
 * stand sie dann im Verlauf, als sei sie verschickt, und der Kunde bekam
 * nichts.
 */
export function ArticleComposer({ ticketId }: ArticleComposerProps) {
  const { t } = useTranslation();
  const create = useCreateArticle(ticketId);
  const uploadFile = useUploadAttachment(ticketId);

  const [articleType, setArticleType] = useState<ArticleType>('note');
  // Je Ticket und je Art ein eigener Zwischenstand: Die interne Notiz und die
  // Antwort an den Kunden sind zwei Texte, und wer zwischen ihnen umschaltet,
  // will nicht den einen im Feld des anderen wiederfinden.
  const [body, setBody, clearBody] = useDraft(`${ticketId}.${articleType}`);
  // Eine Liste statt zweier gleich langer Arrays: Kennung und Name gehoeren
  // zusammen, und ein Anhang, der aus der einen Liste faellt und in der
  // anderen bleibt, ist ein Fehler, der erst beim Absenden auffaellt.
  const [attachments, setAttachments] = useState<{ id: string; filename: string }[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const editorId = 'composer-body';

  const attach = (file: File) => {
    uploadFile.mutate(file, {
      onSuccess: (attachment) => {
        setAttachments((current) => [
          ...current,
          { id: attachment.id, filename: attachment.filename },
        ]);
      },
    });
  };

  const isReply = articleType === 'outgoing_email';
  const visibility: ArticleVisibility = isReply ? 'public' : 'internal';

  const defaults = useReplyDefaults(ticketId, isReply);
  // ``null`` heisst: unveraendert die Vorbelegung. Erst die erste Aenderung
  // macht daraus eine eigene Auswahl -- sonst ueberschriebe das Laden der
  // Vorbelegung, was der Bearbeiter schon eingetragen hat.
  const [chosen, setChosen] = useState<Recipients | null>(null);
  const [pending, setPending] = useState<Record<keyof Recipients, string>>(NO_PENDING);
  const recipients: Recipients = chosen ?? {
    to: defaults.data?.to_addresses ?? [],
    cc: defaults.data?.cc_addresses ?? [],
    bcc: [],
  };
  const contacts = defaults.data?.contacts ?? [];
  const setField = (field: keyof Recipients) => (addresses: string[]) => {
    setChosen({ ...recipients, [field]: addresses });
  };
  // Was beim Absenden hinausgeht: die Kacheln und ein getippter, gueltiger Rest.
  const final = (field: keyof Recipients) =>
    isAddress(pending[field])
      ? [...recipients[field], pending[field].trim().toLowerCase()]
      : recipients[field];
  const pendingInvalid = (Object.keys(pending) as (keyof Recipients)[]).some(
    (field) => pending[field].trim() !== '' && !isAddress(pending[field]),
  );
  const noRecipient = isReply && final('to').length === 0;

  const reset = () => {
    clearBody();
    setChosen(null);
    setPending(NO_PENDING);
    setAttachments([]);
    if (fileInput.current) fileInput.current.value = '';
  };

  const submit = (asDraft: boolean) => {
    create.mutate(
      {
        article_type: articleType,
        visibility,
        body_html: body,
        is_draft: asDraft,
        ...(isReply
          ? {
              to_addresses: final('to'),
              cc_addresses: final('cc'),
              bcc_addresses: final('bcc'),
            }
          : {}),
        attachment_ids: attachments.map((attachment) => attachment.id),
      },
      { onSuccess: reset },
    );
  };

  // Ein Kuerzel auf der Ticketseite waehlt die Art und springt hierher. Der
  // Weg ueber ein Ereignis und nicht ueber eine Eigenschaft: Der Verfasser
  // liegt zwei Ebenen tiefer in einem Reiter, und der Zustand haette sonst
  // durch beide durchgereicht werden muessen, um an genau einer Stelle
  // gebraucht zu werden.
  useEffect(() => {
    const onCompose = (event: Event) => {
      const detail = (event as CustomEvent<ComposeRequest>).detail;
      setArticleType(detail.articleType);
      // Nach dem Wechsel der Art, nicht davor: Der Reiter muss erst
      // erscheinen, sonst greift der Sprung ins Leere.
      window.setTimeout(() => {
        document.getElementById(editorId)?.focus();
      }, 0);
    };

    window.addEventListener(COMPOSE_EVENT, onCompose);
    return () => {
      window.removeEventListener(COMPOSE_EVENT, onCompose);
    };
  }, []);

  return (
    <section className={`composer composer--${visibility}`}>
      {/* Die Wahl der Art traegt Sinnbild **und** Farbe -- dieselben wie im
          Verlauf darunter. Wer gleich eine Antwort schreibt, soll sie
          hinterher wiedererkennen. */}
      <div className="composer__kinds" role="group" aria-label={t('articles.kind')}>
        {(
          [
            ['note', 'note', t('articles.note')],
            ['outgoing_email', 'mailOut', t('articles.reply')],
            ['phone', 'phone', t('articles.phone')],
          ] as const
        ).map(([kind, icon, label]) => (
          <button
            key={kind}
            type="button"
            className={`composer__kind${articleType === kind ? ` composer__kind--on composer__kind--${kind}` : ''}`}
            aria-pressed={articleType === kind}
            onClick={() => {
              setArticleType(kind);
            }}
          >
            <Icon name={icon} />
            {label}
          </button>
        ))}

        <span className="shell__spacer" />

        <SnippetPicker
          ticketId={ticketId}
          onInsert={(text) => {
            setBody((current) => (current ? `${current}${text}` : text));
          }}
        />
      </div>

      {/* Der Hinweis sitzt zwischen Wahl und Schreibbereich und traegt die
          Farbe der Wahl: Er ist die letzte Stelle, an der ein Bearbeiter
          merkt, dass er im falschen Feld schreibt. */}
      <p className={`composer__banner composer__banner--${visibility}`}>
        <Icon name={isReply ? 'mailOut' : 'lock'} />
        {isReply ? t('articles.replyHint') : t('articles.internalHint')}
      </p>

      {isReply ? (
        <div className="composer__recipients" role="group" aria-label={t('articles.recipients')}>
          {(
            [
              ['to', t('articles.to')],
              ['cc', t('articles.cc')],
              ['bcc', t('articles.bcc')],
            ] as const
          ).map(([field, label]) => (
            <RecipientField
              key={field}
              id={`composer-${field}`}
              label={label}
              addresses={recipients[field]}
              onChange={setField(field)}
              contacts={contacts}
              taken={(['to', 'cc', 'bcc'] as const)
                .filter((other) => other !== field)
                .flatMap((other) => recipients[other])}
              pending={pending[field]}
              onPending={(text) => {
                setPending((current) => ({ ...current, [field]: text }));
              }}
            />
          ))}
          {noRecipient && !defaults.isPending ? (
            <p className="field__hint">{t('articles.noRecipient')}</p>
          ) : null}
          {recipients.bcc.length > 0 ? (
            <p className="field__hint">{t('articles.bccHint')}</p>
          ) : null}
        </div>
      ) : null}

      <label className="field__label" htmlFor={editorId}>
        {t('articles.body')}
      </label>

      <RichTextEditor
        id={editorId}
        value={body}
        onChange={setBody}
        ariaLabel={t('articles.body')}
        placeholder={t('articles.bodyPlaceholder')}
        onUploadImage={async (file) => {
          const attachment = await uploadFile.mutateAsync(file);
          return `/api/v1/attachments/${attachment.id}`;
        }}
        onSubmit={() => {
          if (body.trim() !== '' && !noRecipient && !pendingInvalid) submit(false);
        }}
      />

      {/* Anhaenge als Kacheln und die Ablageflaeche daneben. Der nackte
          Dateiwaehler des Browsers stand vorher unter dem Textfeld und sah
          aus wie ein Fremdkoerper -- und er sagte nicht, dass man eine Datei
          auch hineinziehen kann. */}
      <div
        className={`composer__attachments${dragging ? ' composer__attachments--over' : ''}`}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => {
          setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          for (const file of Array.from(event.dataTransfer.files)) attach(file);
        }}
      >
        {attachments.map((attachment) => (
          <span key={attachment.id} className="composer__chip">
            <Icon name="paperclip" />
            {attachment.filename}
            <button
              type="button"
              className="composer__chip-remove"
              aria-label={t('articles.removeAttachment')}
              onClick={() => {
                setAttachments((current) => current.filter((item) => item.id !== attachment.id));
              }}
            >
              <Icon name="close" />
            </button>
          </span>
        ))}

        <button type="button" className="composer__drop" onClick={() => fileInput.current?.click()}>
          <Icon name="upload" />
          {t('articles.dropHint')}
        </button>

        <input
          ref={fileInput}
          type="file"
          hidden
          aria-label={t('articles.addAttachment')}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) attach(file);
          }}
        />
      </div>

      <div className="composer__foot">
        <span className="shell__spacer" />

        <span className="composer__shortcut">{t('articles.sendShortcut')}</span>

        <Button
          variant="secondary"
          disabled={!body.trim() || pendingInvalid}
          busy={create.isPending && create.variables.is_draft}
          onClick={() => {
            submit(true);
          }}
        >
          {t('articles.saveDraft')}
        </Button>
        <Button
          variant="primary"
          disabled={(!body.trim() && attachments.length === 0) || noRecipient || pendingInvalid}
          busy={create.isPending && !create.variables.is_draft}
          onClick={() => {
            submit(false);
          }}
        >
          <Icon name={isReply ? 'send' : 'note'} />
          {isReply ? t('articles.send') : t('articles.addNote')}
        </Button>
      </div>

      {uploadFile.isError ? <ErrorNotice error={uploadFile.error} /> : null}
      {create.isError ? <ErrorNotice error={create.error} /> : null}
    </section>
  );
}
